<script>
  import { format } from 'svelte-i18n';

  import SelectableList from './selectable-list.svelte';

  /**
   * Array of routes to display in the list.
   * @type {Array}
   * @default []
   */
  export let routes = [];

  /**
   * ID of the currently selected route.
   * @type {string | null}
   * @default null
   */
  export let selectedRouteId = null;

  /**
   * Callback function invoked when a route is selected.
   * @type {Function}
   * @default () => {}
   */
  export let onSelectRoute = () => {};

  /**
   * Controls whether tags are displayed in the route list.
   * @type {boolean}
   * @default false
   */
  export let showTags = false;

  /**
   * Label used to describe the route type in the UI.
   * @type {string}
   * @default 'Route'
   */
  export let routeTypeLabel = 'Route';

  /**
   * Controls whether the loading spinner is displayed.
   * @type {boolean}
   * @default false
   */
  export let loading = false;
</script>

<div class="routing-list">
  <div class="routes-list">
    <SelectableList
      items={routes}
      selectedId={selectedRouteId}
      onSelect={onSelectRoute}
      emptyMessage="No routes found"
      itemClass="route-item"
      {loading}
      let:item
    >
      {@const hasTags = showTags && item?.tagblock?.params?.length}
      {@const hasGroups = item.sendergroups?.length > 0}
      {@const hasSelectedTags = showTags && item.selectedTags?.length > 0}

      <div class="route-content" class:groups={hasGroups} class:tags={hasSelectedTags}>
        <div class="priority-badge" class:active={item.status}>{item.priority}</div>

        <div class="route-section">
          <b>{showTags ? (hasTags ? 'Signature' : 'Banner') : routeTypeLabel}</b>
          <div class="blockname">{item.blockname}</div>
        </div>

        {#if hasGroups}
          <div class="route-section">
            <b>{$format('label.GROUPS')}</b>
            <div class="items">
              {#each item.sendergroups as group}
                <div class="item">{group.groupname}</div>
              {/each}
            </div>
          </div>
        {/if}

        {#if hasSelectedTags}
          <div class="route-section">
            <b>{$format('label.TAGS')}</b>
            <div class="items">
              {#each item.selectedTags as tag}
                <div class="item">{tag.value}</div>
              {/each}
            </div>
          </div>
        {/if}
      </div>
    </SelectableList>
  </div>
</div>

<style lang="scss">
  .routing-list {
    label {
      display: block;
      font-weight: var(--font-weight-medium);
      margin-bottom: var(--margin-base);
    }

    .routes-list {
      max-height: 650px;
      overflow-y: auto;

      :global(.route-item) {
        padding: var(--padding-large) var(--padding-xlarge);
      }

      .route-content {
        align-items: center;
        display: grid;
        font-size: var(--font-size-medium);
        gap: 24px;
        grid-template-columns: 32px minmax(min-content, 3fr);
        position: relative;

        &.tags,
        &.groups {
          grid-template-columns: 32px 170px minmax(min-content, 200px) minmax(min-content, 1fr);
        }

        &.tags.groups {
          grid-template-columns: 32px 170px minmax(min-content, 200px) minmax(min-content, 1fr) minmax(min-content, 1fr);
        }

        .priority-badge {
          align-items: center;
          background-color: var(--color-gray-400);
          border-radius: var(--border-radius-base);
          color: var(--color-white);
          display: inline-flex;
          font-weight: var(--font-weight-medium);
          height: 30px;
          justify-content: center;
          padding: var(--padding-small) var(--padding-base);
          transition: all 0.3s ease-in-out;
          width: 30px;

          &.active {
            background-color: var(--color-blue-400);
          }
        }

        .route-section {
          > b {
            display: block;
            font-size: var(--font-size-medium);
            margin-bottom: var(--margin-base);
          }

          .items {
            align-items: center;
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
          }

          .item {
            border: 1px solid var(--border-color);
            border-radius: var(--border-radius-base);
            display: inline-flex;
            font-size: var(--font-size-small);
            padding: var(--padding-small) var(--padding-base);
          }
        }

        .blockname {
          border-radius: var(--border-radius-base);
          color: var(--color-text);
          display: inline-flex;
          font-size: var(--font-size-small);
          padding: var(--padding-small) var(--padding-base);
        }
      }
    }
  }
</style>
